import { FC, useCallback, useRef, useState } from 'react';

import SVG from 'react-inlinesvg';
import { Popover } from 'react-tiny-popover';

import ellipsisSvg from 'assets/icons/ellipsis.svg';
import editSvg from 'assets/icons/edit.svg';
import copySvg from 'assets/icons/copy.svg';

import IconButton from 'lib/IconButton';
import { ButtonSize } from 'lib/IconButton/IconButton.types';
import { FrontEndType } from 'components/common/types/Deal.types';
import { FeatureFlags } from 'components/common/types/Features.types';
import { useNavigate } from 'react-router-dom';
import { useDuplicateDeal } from 'customHooks/useDuplicateDeal';
import { useHasFeatureAccess } from 'customHooks/useHasFeatureAccess';
import { DealItemData } from 'components/common/types/DealItem';

const HIDE_DELAY_MS = 1000;

const ActionRenderer: FC<{ data: DealItemData }> = ({ data }) => {
  const navigate = useNavigate();
  const { handleDuplicateDeal } = useDuplicateDeal();
  const hasOnlyAllowPlanner = useHasFeatureAccess(FeatureFlags.ONLY_ALLOW_PLANNER);

  const { internalId, frontEndType, bookingStatusCode, dealId } = data;
  const [menuVisible, setMenuVisible] = useState(false);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelHideTimeout = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  const startHideTimeout = useCallback(() => {
    cancelHideTimeout();
    hideTimeoutRef.current = setTimeout(() => {
      setMenuVisible(false);
    }, HIDE_DELAY_MS);
  }, [cancelHideTimeout]);

  const handleMouseEnter = useCallback(() => {
    cancelHideTimeout();
    setMenuVisible(true);
  }, [cancelHideTimeout]);

  const handleMouseLeave = useCallback(() => {
    startHideTimeout();
  }, [startHideTimeout]);

  if (!internalId) return null;

  const onEditButtonClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
    e.preventDefault();
    if (hasOnlyAllowPlanner || frontEndType === FrontEndType.PLANNER) {
      navigate(`/planner/programmatic/${internalId}`);
      return;
    }

    navigate(`/deal/${internalId}`);
  };

  const onDuplicateButtonClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
    e.preventDefault();
    handleDuplicateDeal(internalId);
  };

  return (
    <Popover
      isOpen={menuVisible}
      align="center"
      positions={['right']}
      onClickOutside={() => setMenuVisible(false)}
      containerClassName="z-50 bg-neutral-50 shadow-sm border border-neutral-300 rounded-md p-1"
      content={() => (
        <div className="flex flex-row gap-1" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
          <IconButton
            icon={editSvg}
            name="Edit campaign"
            buttonSize={ButtonSize.MEDIUM}
            dataTestId={`edit-${bookingStatusCode}`}
            handleOnClick={onEditButtonClick}
          />
          <IconButton
            icon={copySvg}
            name="Duplicate campaign"
            buttonSize={ButtonSize.MEDIUM}
            dataTestId={`duplicate-${dealId}`}
            handleOnClick={onDuplicateButtonClick}
          />
        </div>
      )}
    >
      <span
        className="flex w-5 h-5 cursor-pointer relative deal-actions-renderer-button shrink-0"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <SVG className="w-5 h-5" src={ellipsisSvg} />
      </span>
    </Popover>
  );
};

export default ActionRenderer;
